import qs from "qs";
import React, { useCallback, useEffect, useState } from "react";
import { PiEmptyBold } from "react-icons/pi";
import { useParams } from "react-router-dom";
import api from "../api";
import RouteMap from "../map/components/RouteMap";
import { RouteDetails } from "../map/types/type";
import { useDetailsFilters } from "./DetailsFilterContext";
import EventTable from "./EventTable";

const RouteDetailsPage: React.FC = () => {
  const { id } = useParams(); // route ID from the URL
  const [routeData, setRouteData] = useState<RouteDetails>();
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("accessToken");

  // Filter state: holds the composite key (e.g. "Marine Thunderstorm Wind-2024-08-06T00:00:00.000Z")
  const [filterGroupKey, setFilterGroupKey] = useState<string | null>(null);

  const { filters, setFilters } = useDetailsFilters();

  const fetchRouteDetails = useCallback(async () => {
    try {
      const queryParams = {
        ...(filters.eventTypes?.length ? { eventTypes: filters.eventTypes.join(',') } : {}),
        ...(filters.startDate ? { startDate: filters.startDate } : {}),
        ...(filters.endDate ? { endDate: filters.endDate } : {})
      };

      // Add a log to debug the parameters
      console.log("Fetching with params:", queryParams);

      // Convert query params to string
      const queryString = qs.stringify(queryParams);

      // Log the actual request URL
      console.log(`Making request to: route/fetch-route/${id}${queryString ? `?${queryString}` : ''}`);

      // Make the API call with query parameters
      const { data: response } = await api.get(`route/fetch-route/${id}${queryString ? `?${queryString}` : ''}`);
      setRouteData(response);
    } catch (error) {
      console.error("Error fetching route details:", error);
    } finally {
      setLoading(false);
    }
  }, [id, filters]);

  useEffect(() => {
    if (id) {
      fetchRouteDetails();
    }
  }, [id, fetchRouteDetails]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 flex flex-col items-center">
        <div className="loader"></div>
      </div>
    );
  }

  if (!routeData) {
    return <p>No route data found for ID: {id}</p>;
  }

  // Filter out "empty" events
  const validEvents = routeData.events.filter(
    (event) => event.EVENT_TYPE !== null && event.EVENT_TYPE !== undefined
  );

  // Further filter events based on the composite key (if one is set)
  const filteredEvents = filterGroupKey
    ? validEvents.filter(
      (event) => `${event.EVENT_TYPE}-${event.date}` === filterGroupKey
    )
    : validEvents;

  return (
    <div style={{ marginTop: "1rem" }}>
      {validEvents.length === 0 ? (
        <div className="bg-white p-8 rounded-xl shadow-lg flex flex-col items-center justify-center">
          <PiEmptyBold className="text-6xl text-gray-300" />
          <h3 className="mt-4 text-2xl font-semibold text-gray-800">
            No Events Found
          </h3>
          <p className="mt-2 text-base text-gray-500 text-center max-w-sm">
            There are no events associated with this route.
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="bg-white rounded-lg shadow-md w-full max-w-7xl p-6 mb-6 h-[500px]">
            <RouteMap
              routes={[
                {
                  id: routeData.route.id.toString(),
                  coords: routeData.route.coords,
                  color: "black",
                  start_loc: routeData.route.start_loc,
                  end_loc: routeData.route.end_loc
                },
              ]}
              events={filteredEvents.map((event) => ({
                eventType: event.EVENT_TYPE,
                coords: { lat: event.BEGIN_LAT, lng: event.BEGIN_LON },
                distance: event.distance,
                date: event.date,
              }))}
              // When an event is clicked in the map, set the filter
              onEventClick={(groupKey) => setFilterGroupKey(groupKey)}
            />
          </div>

          <EventTable
            route={routeData}
            filterGroupKey={filterGroupKey}
            clearFilter={() => setFilterGroupKey(null)}
            // New prop: when a group is toggled open in the table, update the filter.
            onGroupFilterChange={(groupKey) => setFilterGroupKey(groupKey)}
          />
        </div>
      )}
    </div>
  );
};

export default RouteDetailsPage;
